import { Box, Button, Chip, Grid, Paper, Typography } from "@mui/material";
import { SubscriptionInfoQuery } from "~/operations";
import { BillingGauge } from "./components";

type SubscriptionInfo = NonNullable<SubscriptionInfoQuery["subscriptionInfo"]>;

type ActiveSubscriptionsProps = {
  billingSessionUrl: string;
  subscriptionInfo: SubscriptionInfo;
  onQuotaRequest: () => void;
};

export function ActiveSubscriptions({
  billingSessionUrl,
  subscriptionInfo,
  onQuotaRequest,
}: ActiveSubscriptionsProps) {
  const { basePlan, utilizationMetrics, pendingCancel } = subscriptionInfo;
  const isCommunityPlan = basePlan.key === "plan_community";
  const isTeamPlan = basePlan.key === "plan_team";
  const isEnterprisePlan = basePlan.key === "plan_enterprise";
  let description = "";
  let buttonText = "";

  if (isCommunityPlan) {
    buttonText = "Upgrade now";
    description =
      "Upgrade your membership today to unlock powerful features and premium support!";
  } else if (isTeamPlan) {
    if (pendingCancel) {
      buttonText = "Renew subscription";
      description =
        "Your subscription has been cancelled but will continue to work through your current billing period.";
    } else {
      buttonText = "Manage subscription";
      description =
        "Manage your payment data, billing information, or cancel your subscription.";
    }
  } else if (isEnterprisePlan) {
    buttonText = "Get in touch";
    description =
      "Please get in touch with us if you want to manage your subscription details, such as adding more assets or buying premium support.";
  }

  const statusChip = pendingCancel ? (
    <Chip label="Cancelled" color="primary" variant="outlined" size="small" />
  ) : null;
  const upgradeMsg = isTeamPlan ? (
    <Grid container p={2} alignItems="center" size={12}>
      <Typography variant="body2" mr={1}>
        Need a bigger quota?
      </Typography>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={onQuotaRequest}
      >
        Contact Us
      </Button>
    </Grid>
  ) : null;

  return (
    <Grid container component={Paper}>
      <Grid
        sx={{
          backgroundColor: "background.light",
          borderRadius: "6px 6px 0px 0px",
          display: "flex",
          alignItems: "center",
          py: 1,
          px: 2,
        }}
        size={12}
      >
        <Typography component="span" variant="h6" color="text.secondary">
          {basePlan.name} Usage
        </Typography>
      </Grid>
      <Grid container>
        <Grid container p={"40px"} pb={0} size={12}>
          <Grid size={8}>
            {statusChip && (
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                {statusChip}
              </Typography>
            )}
            <Typography>{description}</Typography>
          </Grid>
          <Grid size={4}>
            <Box
              height={1}
              display="flex"
              alignItems="flex-start"
              justifyContent="right"
            >
              {billingSessionUrl && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  href={billingSessionUrl}
                >
                  {buttonText}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
        <Grid container px={"40px"} pb={"40px"} pt={0} size={12}>
          <Grid display="flex" flexDirection="row" size={12}>
            {utilizationMetrics?.map((m) => (
              <BillingGauge
                key={m.name}
                title={m.name}
                value={m.value}
                total={m.limit || 0}
              />
            ))}
          </Grid>
        </Grid>
        {upgradeMsg}
      </Grid>
    </Grid>
  );
}
